<template>
	<div
		:class="[
			'form-group',
			{
				filled: isFilled,
				focused,
				disabled,
			},
		]"
	>
		<div class="field-label-container">
			<div
				v-if="!omitInfo"
				class="info-anchor"
			>
				<button
					ref="tooltipTrigger"
					class="info-button"
					:class="{
						active: $slots.tooltip,
					}"
					type="button"
					:disabled="disabled"
					@click.prevent="ToggleToolTip"
				>
					<i class="fal fa-info-circle mr-2 tooltip-icon"></i>
				</button>

				<div
					v-show="showToolTip"
					ref="tooltipPopup"
					class="info-inline-popup"
				>
					<slot name="tooltip"></slot>
				</div>
			</div>
			<label class="field-label">
				{{ label }}
				<span
					v-if="required"
					class="required-mark"
				>
					*
				</span>
			</label>
		</div>
		<div class="w-full flex items-center">
			<DocAutocomplete
				:value="rg"
				class="doc-autocomplete"
				:class="resolvedInputClasses"
				:options="optionsDoc"
				placeholder="DIGITE O RG"
				:disabled="disabled"
				:readonly="readonly"
				:input-classes="inputClasses"
				:entity-key="entityKey"
				:formatter="formatter"
				@focus="focusedDoc = true"
				@blur="focusedDoc = false"
				@select="$emit('select-doc', $event)"
				@input="$emit('input-doc', $event)"
			/>
			<Autocomplete
				:value="uf"
				class="uf-autocomplete"
				:class="resolvedInputClasses"
				:options="optionsUf"
				placeholder="UF"
				:min-length="minLength"
				:disabled="disabled"
				:readonly="readonly"
				:input-classes="inputClasses"
				@focus="focusedUf = true"
				@blur="focusedUf = false"
				@select="$emit('input-uf', $event)"
				@input="$emit('input-uf', $event)"
			>
				<template #buttons>
					<slot name="buttons"></slot>
				</template>
				<template #option="{ option }">
					<slot
						name="option"
						:option="option"
					></slot>
				</template>
			</Autocomplete>
		</div>
	</div>
</template>

<script>
	import { createPopper } from '@popperjs/core'

	export default {
		name: 'FormGroupRG',
		components: {},
		props: {
			rg: {
				type: [String, null],
				default: '',
			},
			uf: {
				type: [String, null],
				default: '',
			},
			inputClasses: {
				type: Array,
				default: () => [],
			},
			label: {
				type: String,
				default: 'RG',
			},
			optionsDoc: {
				type: Array,
				default: () => [],
			},
			optionsUf: {
				type: Array,
				default: () => [],
			},
			interceptSelect: {
				type: Boolean,
				default: false,
			},
			required: {
				type: Boolean,
				default: false,
			},
			disabled: {
				type: Boolean,
				default: false,
			},
			readonly: {
				type: Boolean,
				default: false,
			},
			minLength: {
				type: [Number, String],
				default: 0,
			},
			maxLength: {
				type: [Number],
				default: null,
			},
			formatter: {
				type: String,
				default: '',
			},
			entityKey: {
				type: [String, Number],
				default: '',
			},
			omitInfo: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				showToolTip: false,
				tooltipPopper: null,
				focusedDoc: false,
				focusedUf: false,
			}
		},
		computed: {
			resolvedInputClasses() {
				return [this.inputClasses].flat(Infinity).filter(Boolean)
			},
			inputClassesString() {
				const classes = []

				for (const item of this.resolvedInputClasses) {
					if (typeof item === 'string') {
						classes.push(item)
					} else if (item && typeof item === 'object') {
						for (const [key, value] of Object.entries(item)) {
							if (value) classes.push(key)
						}
					}
				}

				return classes.join(' ')
			},
			isFilled() {
				return this.rg?.toString().length > 0 || this.uf?.toString().length > 0
			},
			focused() {
				return this.focusedDoc || this.focusedUf
			},
		},
		watch: {
			showToolTip(value) {
				if (value) {
					this.$nextTick(() => {
						this.CreateTooltipPopper()

						this.tooltipPopper?.update()
					})

					return
				}

				this.DestroyTooltipPopper()
			},
			resolvedInputClasses: {
				handler() {
					if (this.inputType !== 'date') return
					this.$nextTick(() => {
						const fp = this.$refs.input?.fp
						if (fp?.altInput) {
							fp.altInput.className = [
								'form-control',
								'flatpickr-alt-input',
								this.inputClassesString,
							].join(' ')
						}
					})
				},
				immediate: true,
			},
		},
		methods: {
			ToggleToolTip() {
				if (this.disabled || !this.$slots.tooltip) {
					return
				}

				this.showToolTip = !this.showToolTip
			},
			CreateTooltipPopper() {
				if (this.tooltipPopper || !this.$refs.tooltipTrigger || !this.$refs.tooltipPopup) {
					return
				}

				this.tooltipPopper = createPopper(this.$refs.tooltipTrigger, this.$refs.tooltipPopup, {
					placement: 'bottom-start',
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 0],
							},
						},
						{
							name: 'flip',
							options: {
								fallbackPlacements: ['top-start'],
								padding: 8,
							},
						},
						{
							name: 'preventOverflow',
							options: {
								padding: 8,
							},
						},
					],
				})
			},
			DestroyTooltipPopper() {
				if (!this.tooltipPopper) {
					return
				}

				this.tooltipPopper.destroy()
				this.tooltipPopper = null
			},
			CloseToolTip() {
				this.showToolTip = false
			},
			FormatInputValue(value) {
				const rawValue = String(value ?? '')

				switch (this.formatter) {
					case 'alphanumeric':
						return rawValue.replace(/[^a-zA-Z0-9À-ÿ\s]/g, '')
					case 'letters':
						return rawValue.replace(/[^a-zA-ZÀ-ÿ\s]/g, '')
					case 'numbers':
						return rawValue.replace(/[^0-9]/g, '')
					case 'cpf': {
						const digits = rawValue.replace(/\D/g, '').slice(0, 11)
						return digits
							.replace(/^(\d{3})(\d)/, '$1.$2')
							.replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
							.replace(/\.(\d{3})(\d)/, '.$1-$2')
					}
					case 'phone': {
						const digits = rawValue.replace(/\D/g, '').slice(0, 11)
						if (digits.length <= 10) {
							return digits.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
						}
						return digits.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
					}
					case 'cep': {
						const digits = rawValue.replace(/\D/g, '').slice(0, 8)
						return digits.replace(/^(\d{5})(\d)/, '$1-$2')
					}
					case 'email':
						return rawValue.replace(/[^a-zA-Z0-9@._-]/g, '')
					default:
						return rawValue
				}
			},
			HandleClick(event) {
				if (!this.showToolTip) {
					return
				}
				const popup = this.$refs.tooltipPopup
				const trigger = this.$refs.tooltipTrigger
				const path = event.composedPath()
				if ((popup && path.includes(popup)) || (trigger && path.includes(trigger))) {
					return
				}
				this.CloseToolTip()
			},
		},
		beforeMount() {},
		mounted() {
			window.addEventListener('click', this.HandleClick)
		},
		beforeDestroy() {
			this.DestroyTooltipPopper()
			window.removeEventListener('click', this.HandleClick)
		},
		beforeUnmount() {
			this.DestroyTooltipPopper()
			window.removeEventListener('click', this.HandleClick)
		},
	}
</script>

<style lang="scss" scoped>
	.form-group {
		--inputBackgroundColor: #223042;
		--inputBorderColor: #314977;
		--iconColor: #4a5b74;
		--alertColor: #eab308;

		.field-label {
			display: flex;
			align-items: center;
			text-transform: uppercase;
			font-size: 0.875rem;
			font-weight: 600;
			color: var(--newPrimaryInitialBackgroundColor);
			margin-bottom: 6px;
			white-space: nowrap;
			padding: 0;
		}

		.input-wrapper {
			position: relative;

			i {
				position: absolute;
				top: 50%;
				transform: translateY(-50%);
				z-index: 5;

				&.clear-icon {
					right: 0.2rem;
					color: var(--newPrimaryBackgroundColor);
					cursor: pointer;
					padding: 0.25rem;

					&:hover {
						color: var(--newPrimaryFocusColor);
					}
				}
			}

			input.form-control[type='email'] {
				text-transform: lowercase;

				&::placeholder {
					text-transform: uppercase;
				}
			}

			textarea.form-control {
				padding: 0.5rem 2rem 0.5rem 0.5rem;
				height: 5.125rem;
				resize: none;
			}

			.readonly-overlay {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				cursor: not-allowed;
			}
		}

		:deep(.doc-autocomplete) {
			width: 100%;

			.form-control {
				border-radius: 0.25rem 0 0 0.25rem;

				&:not(:disabled) {
					&.field-error {
						border-right: 1px solid var(--alertColor) !important;
					}
				}
			}
		}
		:deep(.uf-autocomplete) {
			width: 25%;

			.form-control {
				border-radius: 0 0.25rem 0.25rem 0;

				&:not(:disabled) {
					&.field-error {
						border-left: 1px solid var(--alertColor) !important;
					}
				}
			}
		}

		.required-mark {
			color: var(--alertColor);
			margin-left: 2px;
		}

		.info-button {
			padding: 0;
			border: none;
			background-color: transparent;
			cursor: pointer;

			i {
				font-size: 1.25rem;
				margin-top: 0.3rem;
				color: #334155;
			}

			&.active {
				i {
					color: var(--newPrimaryFocusBackgroundColor);
				}

				&:hover {
					i {
						color: var(--fontColorHover);
					}
				}
			}

			&:disabled {
				cursor: default;

				i {
					color: var(--disabledColor);
				}
			}
		}

		.field-label-container {
			display: flex;
			align-items: center;
			gap: 0.2rem;

			.field-label {
				min-height: 28px;
				margin-bottom: 0;
			}

			.info-anchor {
				position: relative;
				display: inline-flex;
				align-items: center;
			}

			.info-inline-popup {
				width: 380px;
				max-width: calc(100vw - 16px);
				position: absolute;
				top: 0;
				left: 0;
				z-index: 1000;
				background-color: var(--tippyBackgroundColor);
				border: 1px solid var(--tippyBorderColor);
				border-radius: 0.5rem;
				box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
				white-space: normal;
				padding: 0.75rem;

				.info-modal-list {
					margin: 0;
					padding: 0;
					list-style: none;

					.info-modal-item {
						padding-top: 0.25rem;
						padding-bottom: 0.25rem;
						color: var(--fontColor);
						font-size: 0.875rem;
					}
				}
			}
		}

		.checkbox-input-wrapper {
			display: flex;
			align-items: center;
			gap: 0.5rem;

			.custom-checkbox {
				-webkit-appearance: none;
				appearance: none;
				width: 2.25rem;
				height: 2.25rem;
				cursor: pointer;
				border-radius: 4px;
				background-color: var(--inputBackgroundColor);
				border: 1px solid var(--inputBorderColor);
				position: relative;
				transition: background-color 0.2s, border-color 0.2s;

				&:checked {
					background-color: var(--backgroundColorActiveSelected);
					border-color: var(--newColor);
				}

				&:checked::after {
					content: '';
					position: absolute;
					top: 40%;
					left: 50%;
					width: 12px;
					height: 22px;
					border: solid var(--fontColorActiveSelected);
					border-width: 0 3px 3px 0;
					transform: translate(-50%, -50%) rotate(45deg);
				}
			}

			label.field-label {
				margin-bottom: 0;
				font-size: 0.75rem;
				cursor: pointer;
			}
		}

		&.filled,
		&.focused {
			:deep(.form-control:not(:read-only)),
			.form-control:not(:read-only) {
				background-color: var(--newPrimaryFocusBackgroundColor);
				color: var(--newPrimaryFocusColor);
				outline: none;
				caret-color: var(--newPrimaryFocusColor);
				padding-right: 1.8rem;

				&::placeholder {
					color: var(--newPrimaryFocusColor);
				}

				&:disabled {
					color: var(--disabledColor);

					&::placeholder {
						color: var(--disabledColor);
					}
				}
			}

			.field-label {
				color: var(--newPrimaryFocusBackgroundColor);
			}
		}

		&.disabled {
			.field-label {
				color: var(--disabledColor);
			}
		}
	}
</style>
